/**
 * Cognito authentication service.
 *
 * Wraps `amazon-cognito-identity-js` to provide a clean async API for
 * sign-up, sign-in, sign-out, session restoration, and token refresh.
 */
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { env } from '../utils/env';

const poolData = {
  UserPoolId: env.COGNITO_USER_POOL_ID,
  ClientId: env.COGNITO_CLIENT_ID,
};

let userPool: CognitoUserPool | null = null;

function getUserPool(): CognitoUserPool {
  if (!userPool) {
    if (!poolData.UserPoolId || !poolData.ClientId) {
      throw new Error(
        'Cognito is not configured. Set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID in .env.local (run ./setup-env.sh).'
      );
    }
    userPool = new CognitoUserPool(poolData);
  }
  return userPool;
}

export interface CognitoTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

export interface CognitoUserInfo {
  userId: string;
  email: string;
  name: string;
  organizationId?: string;
  groups: string[];
}

export interface AuthResult {
  tokens: CognitoTokens;
  user: CognitoUserInfo;
}

function parseCognitoGroups(raw: unknown): string[] {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) {
    return raw.map((g) => String(g).trim()).filter(Boolean);
  }
  const text = String(raw).trim();
  if (!text) return [];
  if (text.startsWith('[') && text.endsWith(']')) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        return parsed.map((g) => String(g).trim()).filter(Boolean);
      }
    } catch {
      /* fall through */
    }
  }
  if (text.includes(',')) {
    return text.split(',').map((g) => g.trim()).filter(Boolean);
  }
  if (text.includes(' ')) {
    return text.split(/\s+/).map((g) => g.trim()).filter(Boolean);
  }
  return [text];
}

function extractUserInfo(session: CognitoUserSession): CognitoUserInfo {
  const idToken = session.getIdToken();
  const payload = idToken.decodePayload();
  return {
    userId: payload['sub'] as string,
    email: (payload['email'] as string) || '',
    name: (payload['name'] as string) || (payload['email'] as string)?.split('@')[0] || '',
    organizationId: (payload['custom:organization-id'] as string) || undefined,
    groups: parseCognitoGroups(payload['cognito:groups']),
  };
}

function extractTokens(session: CognitoUserSession): CognitoTokens {
  return {
    idToken: session.getIdToken().getJwtToken(),
    accessToken: session.getAccessToken().getJwtToken(),
    refreshToken: session.getRefreshToken().getToken(),
  };
}

export function signUp(
  email: string,
  password: string,
  name: string
): Promise<{ userSub: string }> {
  return new Promise((resolve, reject) => {
    const attributes = [
      new CognitoUserAttribute({ Name: 'email', Value: email }),
      new CognitoUserAttribute({ Name: 'name', Value: name }),
    ];

    getUserPool().signUp(email, password, attributes, [], (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve({ userSub: result?.userSub ?? '' });
    });
  });
}

export function confirmSignUp(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: getUserPool(),
    });
    cognitoUser.confirmRegistration(code, true, (err) => {
      if (err) {
        reject(err);
        return;
      }
      resolve();
    });
  });
}

export function signIn(email: string, password: string): Promise<AuthResult> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: getUserPool(),
    });

    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => {
        resolve({
          tokens: extractTokens(session),
          user: extractUserInfo(session),
        });
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
}

export function signOut(): void {
  const cognitoUser = getUserPool().getCurrentUser();
  if (cognitoUser) {
    cognitoUser.signOut();
  }
}

export function getCurrentSession(): Promise<AuthResult | null> {
  return new Promise((resolve) => {
    const cognitoUser = getUserPool().getCurrentUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }

    cognitoUser.getSession(
      (err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          resolve(null);
          return;
        }
        resolve({
          tokens: extractTokens(session),
          user: extractUserInfo(session),
        });
      }
    );
  });
}

/**
 * Refresh Cognito tokens for the current user.
 *
 * Prefer getSession() which already refreshes expired ID/access tokens.
 * Only force refreshSession when the returned session is still invalid
 * (avoids concurrent double-refresh races).
 */
export function refreshSession(): Promise<AuthResult | null> {
  return new Promise((resolve) => {
    const cognitoUser = getUserPool().getCurrentUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }

    cognitoUser.getSession(
      (err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          resolve(null);
          return;
        }

        if (session.isValid()) {
          resolve({
            tokens: extractTokens(session),
            user: extractUserInfo(session),
          });
          return;
        }

        const refreshToken = session.getRefreshToken();
        cognitoUser.refreshSession(
          refreshToken,
          (refreshErr: Error | null, newSession: CognitoUserSession) => {
            if (refreshErr || !newSession || !newSession.isValid()) {
              resolve(null);
              return;
            }
            resolve({
              tokens: extractTokens(newSession),
              user: extractUserInfo(newSession),
            });
          }
        );
      }
    );
  });
}

export function resendConfirmationCode(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: getUserPool(),
    });
    cognitoUser.resendConfirmationCode((err) => {
      if (err) {
        reject(err);
        return;
      }
      resolve();
    });
  });
}

export function forgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: getUserPool(),
    });
    cognitoUser.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

export function confirmNewPassword(
  email: string,
  code: string,
  newPassword: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: getUserPool(),
    });
    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

export function changePassword(
  oldPassword: string,
  newPassword: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = getUserPool().getCurrentUser();
    if (!cognitoUser) {
      reject(new Error('No authenticated user'));
      return;
    }

    cognitoUser.getSession((err: Error | null) => {
      if (err) {
        reject(err);
        return;
      }
      cognitoUser.changePassword(oldPassword, newPassword, (changeErr) => {
        if (changeErr) {
          reject(changeErr);
          return;
        }
        resolve();
      });
    });
  });
}
